import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Phone,
  Mail,
  Calendar,
  FileText,
  Plus,
  ChevronDown,
  Filter,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import {
  LogCallModal,
  SendEmailModal,
  ScheduleMeetingModal,
  AddNoteModal,
  type NewActivity,
} from '@/components/modals/ActivityModals';
import { cn } from '@/lib/utils';
import {
  GET_ACTIVITIES,
  CREATE_ACTIVITY,
  type Activity,
  type ActivityType as GqlActivityType,
  type GetActivitiesData,
  type GetActivitiesVariables,
  type CreateActivityInput,
} from '@/graphql/activities';

type ActivityType = 'call' | 'email' | 'meeting' | 'note';

const ACTIVITY_ICONS: Record<ActivityType, typeof Phone> = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: FileText,
};

const ACTIVITY_OPTIONS = [
  { type: 'call', label: 'Log Call', icon: Phone },
  { type: 'email', label: 'Send Email', icon: Mail },
  { type: 'meeting', label: 'Schedule Meeting', icon: Calendar },
  { type: 'note', label: 'Add Note', icon: FileText },
];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Activities' },
  { value: 'CALL', label: 'Calls' },
  { value: 'EMAIL', label: 'Emails' },
  { value: 'MEETING', label: 'Meetings' },
  { value: 'NOTE', label: 'Notes' },
];

interface ActivitiesTabProps {
  clientId: string;
  clientName: string;
  contactEmail: string;
}

export function ActivitiesTab({ clientId, clientName, contactEmail }: ActivitiesTabProps) {
  const [showActivityMenu, setShowActivityMenu] = useState(false);
  const [activeModal, setActiveModal] = useState<ActivityType | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const activityMenuRef = useRef<HTMLDivElement>(null);

  // GraphQL query for activities
  const { data, loading, error, refetch } = useQuery<GetActivitiesData, GetActivitiesVariables>(
    GET_ACTIVITIES,
    {
      variables: {
        organizationId: clientId,
        type: filterType !== 'all' ? (filterType as GqlActivityType) : undefined,
        limit: 50,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // GraphQL mutation for creating activities
  const [createActivity, { loading: creating }] = useMutation(CREATE_ACTIVITY, {
    refetchQueries: [
      {
        query: GET_ACTIVITIES,
        variables: {
          organizationId: clientId,
          type: filterType !== 'all' ? (filterType as GqlActivityType) : undefined,
          limit: 50,
        },
      },
    ],
    onCompleted: () => {
      setActiveModal(null);
    },
    onError: (error) => {
      console.error('Failed to create activity:', error);
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (activityMenuRef.current && !activityMenuRef.current.contains(event.target as Node)) {
        setShowActivityMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activities = data?.activities?.items || [];

  // Map GraphQL type to lowercase for display
  const mapTypeForDisplay = (type: string): ActivityType => {
    return type.toLowerCase() as ActivityType;
  };

  // Count activities by type (all activities, not just filtered)
  const getActivityCounts = () => {
    // When filtering, we need to count from all activities
    // For simplicity, we'll count from the current data
    return {
      call: activities.filter((a) => a.type === 'CALL').length,
      email: activities.filter((a) => a.type === 'EMAIL').length,
      meeting: activities.filter((a) => a.type === 'MEETING').length,
      note: activities.filter((a) => a.type === 'NOTE').length,
    };
  };

  const counts = getActivityCounts();

  const handleActivitySubmit = async (activity: NewActivity) => {
    // Map the local activity type to GraphQL enum
    const typeMap: Record<string, GqlActivityType> = {
      call: 'CALL',
      email: 'EMAIL',
      meeting: 'MEETING',
      note: 'NOTE',
    };

    const input: CreateActivityInput = {
      organizationId: clientId,
      type: typeMap[activity.type],
      description: activity.description,
      activityDate: activity.date.toISOString(),
      metadata: {
        // Call specific
        callDirection: activity.type === 'call' ? 'outbound' : undefined,
        // Email specific
        subject: activity.subject,
        recipients: activity.recipients,
        body: activity.body,
        // Meeting specific
        meetingTitle: activity.meetingTitle,
        startTime: activity.startTime,
        endTime: activity.endTime,
        location: activity.location,
        attendees: activity.attendees,
      },
    };

    await createActivity({ variables: { input } });
  };

  // Get contact name from activity
  const getContactName = (activity: Activity): string | undefined => {
    if (activity.contact) {
      return `${activity.contact.firstName} ${activity.contact.lastName}`;
    }
    return undefined;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate dark:text-mist">Activities</h2>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            Communication history and logged activities
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            disabled={loading}
            className="btn-ghost btn-icon"
            title="Refresh activities"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </button>

          {/* Filter Dropdown */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="select py-1.5 text-sm w-40"
            >
              {FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Log Activity Button */}
          <div className="relative" ref={activityMenuRef}>
            <button
              onClick={() => setShowActivityMenu(!showActivityMenu)}
              className="btn-primary"
              disabled={creating}
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Log Activity
              <ChevronDown className="w-3 h-3 ml-1" />
            </button>
            {showActivityMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-navy rounded-lg shadow-lg border border-gray dark:border-night-pond py-1 z-10">
                {ACTIVITY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.type}
                      onClick={() => {
                        setShowActivityMenu(false);
                        setActiveModal(option.type as ActivityType);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate dark:text-mist hover:bg-pale-gray-2 dark:hover:bg-night-pond transition-colors"
                    >
                      <Icon className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-rust/10 border border-rust/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rust" />
          <div>
            <p className="text-sm font-medium text-rust">Failed to load activities</p>
            <p className="text-xs text-rust/80">{error.message}</p>
          </div>
          <button onClick={() => refetch()} className="ml-auto btn-secondary btn-sm">
            Retry
          </button>
        </div>
      )}

      {/* Activity Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-pond dark:text-pond-light" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {loading ? '—' : counts.call}
                </p>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Calls</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-turquoise/10 dark:bg-aqua/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-turquoise dark:text-aqua" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {loading ? '—' : counts.email}
                </p>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Emails</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-violet dark:text-violet-light" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {loading ? '—' : counts.meeting}
                </p>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Meetings</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange/10 dark:bg-orange/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate dark:text-mist">
                  {loading ? '—' : counts.note}
                </p>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Notes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-slate dark:text-mist">
            Activity Timeline
            {filterType !== 'all' && (
              <span className="text-sm font-normal text-chalkboard dark:text-rain-cloud ml-2">
                (Showing {FILTER_OPTIONS.find((o) => o.value === filterType)?.label})
              </span>
            )}
          </h3>
        </div>
        <div className="card-body">
          {loading && activities.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-pond dark:text-pond-light" />
              <span className="ml-2 text-chalkboard dark:text-rain-cloud">
                Loading activities...
              </span>
            </div>
          ) : activities.length === 0 ? (
            <p className="text-center text-chalkboard dark:text-rain-cloud py-8">
              No activities found. {filterType !== 'all' && 'Try changing the filter or '}
              Click &quot;Log Activity&quot; to add one.
            </p>
          ) : (
            <div className="space-y-4">
              {activities.map((activity, index) => {
                const displayType = mapTypeForDisplay(activity.type);
                const ActivityIcon = ACTIVITY_ICONS[displayType];
                const isLast = index === activities.length - 1;
                const contactName = getContactName(activity);
                return (
                  <div key={activity.id} className="flex gap-3">
                    <div className="relative">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-full flex items-center justify-center',
                          displayType === 'call' && 'bg-pond/10 dark:bg-pond-light/20',
                          displayType === 'email' && 'bg-turquoise/10 dark:bg-aqua/20',
                          displayType === 'meeting' && 'bg-violet/10 dark:bg-violet-light/20',
                          displayType === 'note' && 'bg-orange/10 dark:bg-orange/20'
                        )}
                      >
                        <ActivityIcon
                          className={cn(
                            'w-5 h-5',
                            displayType === 'call' && 'text-pond dark:text-pond-light',
                            displayType === 'email' && 'text-turquoise dark:text-aqua',
                            displayType === 'meeting' && 'text-violet dark:text-violet-light',
                            displayType === 'note' && 'text-orange'
                          )}
                        />
                      </div>
                      {!isLast && (
                        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray dark:bg-night-pond" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate dark:text-mist capitalize">
                            {displayType}
                          </span>
                          {contactName && (
                            <>
                              <span className="text-xs text-chalkboard dark:text-rain-cloud">
                                with
                              </span>
                              <span className="text-sm text-pond dark:text-pond-light">
                                {contactName}
                              </span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-chalkboard dark:text-rain-cloud">
                          {formatRelativeTime(new Date(activity.activityDate))}
                        </span>
                      </div>
                      <p className="text-sm text-slate dark:text-mist mt-1">
                        {activity.description}
                      </p>
                      <p className="text-xs text-chalkboard dark:text-rain-cloud mt-2">
                        by {activity.createdBy}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Activity Modals */}
      <LogCallModal
        isOpen={activeModal === 'call'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleActivitySubmit}
        prospectName={clientName}
        contactEmail={contactEmail}
      />
      <SendEmailModal
        isOpen={activeModal === 'email'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleActivitySubmit}
        prospectName={clientName}
        contactEmail={contactEmail}
        organizationId={clientId}
      />
      <ScheduleMeetingModal
        isOpen={activeModal === 'meeting'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleActivitySubmit}
        prospectName={clientName}
        contactEmail={contactEmail}
      />
      <AddNoteModal
        isOpen={activeModal === 'note'}
        onClose={() => setActiveModal(null)}
        onSubmit={handleActivitySubmit}
        prospectName={clientName}
        contactEmail={contactEmail}
      />
    </div>
  );
}
